import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Globe, User, Building2, Mail, Phone, ArrowRight, Check, ArrowLeft, CheckCircle2 } from 'lucide-react';
import BrandLockup from '../components/BrandLockup';
import apiService from '../services/api';

interface ContactForm {
  name: string;
  company: string;
  email: string;
  phone: string;
  message: string;
  website: string; // honeypot — must stay empty
}

const Contact: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [sending, setSending] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ContactForm>({
    defaultValues: { name: '', company: '', email: '', phone: '', message: '', website: '' },
  });

  const changeLanguage = (lng: string) => i18n.changeLanguage(lng);

  const onSubmit = async (data: ContactForm) => {
    try {
      setSubmitError('');
      setSending(true);
      await apiService.post('/contact', data);
      setSubmitted(true);
    } catch (error: any) {
      setSubmitError(error.response?.data?.message || t('contact.error'));
    } finally {
      setSending(false);
    }
  };

  const highlights = [
    t('auth.highlightMonitoring'),
    t('auth.highlightOperations'),
    t('auth.highlightWorkforce'),
  ];

  const inputClass =
    'w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-3 text-sm text-gray-900 placeholder:text-gray-400 transition focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20';

  return (
    <div className="min-h-screen flex bg-white">
      {/* ── Brand panel (desktop) ── */}
      <div className="relative hidden lg:flex lg:w-[46%] xl:w-1/2 overflow-hidden text-white bg-gradient-to-br from-[#0060b9] via-[#0055a9] to-[#002344]">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(circle at 22% 18%, rgba(0,165,189,0.38), transparent 52%), radial-gradient(circle at 85% 90%, rgba(0,138,187,0.30), transparent 46%)',
          }}
        />
        <svg
          className="pointer-events-none absolute -bottom-16 -right-10 w-[420px] opacity-[0.08]"
          viewBox="0 0 64 64"
          fill="none"
          aria-hidden
        >
          <rect x="3" y="8" width="8" height="24" rx="4" fill="#ffffff" />
          <rect x="15.5" y="8" width="8" height="36" rx="4" fill="#ffffff" />
          <rect x="28" y="8" width="8" height="48" rx="4" fill="#ffffff" />
          <rect x="40.5" y="8" width="8" height="36" rx="4" fill="#ffffff" />
          <rect x="53" y="8" width="8" height="24" rx="4" fill="#ffffff" />
        </svg>

        <div className="relative z-10 flex w-full flex-col justify-between p-12 xl:p-16">
          <BrandLockup variant="light" size={44} />
          <div className="max-w-md">
            <h1 className="text-4xl xl:text-5xl font-bold leading-[1.1] tracking-tight">
              {t('auth.brandHeadline')}
            </h1>
            <p className="mt-5 text-base leading-relaxed text-white/70">{t('auth.brandSubtitle')}</p>
            <ul className="mt-9 space-y-3.5">
              {highlights.map((line) => (
                <li key={line} className="flex items-center gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/15">
                    <Check className="h-3.5 w-3.5 text-white" aria-hidden />
                  </span>
                  <span className="text-sm text-white/90">{line}</span>
                </li>
              ))}
            </ul>
          </div>
          <p className="text-sm text-white/45">© {new Date().getFullYear()} VueNexa Enterprise</p>
        </div>
      </div>

      {/* ── Form panel ── */}
      <div className="flex flex-1 flex-col min-h-screen">
        <div className="flex justify-end p-5 sm:p-6">
          <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-sm">
            <Globe className="h-4 w-4 text-gray-400" aria-hidden />
            <label htmlFor="lang" className="sr-only">
              {t('common.language')}
            </label>
            <select
              id="lang"
              value={i18n.language}
              onChange={(e) => changeLanguage(e.target.value)}
              className="cursor-pointer border-0 bg-transparent pr-1 text-sm font-medium text-gray-700 focus:outline-none focus:ring-0"
            >
              <option value="en">English</option>
              <option value="gu">ગુજરાતી</option>
              <option value="hi">हिन्दी</option>
            </select>
          </div>
        </div>

        <div className="flex flex-1 items-center justify-center px-6 pb-12">
          <div className="w-full max-w-md">
            <div className="mb-8 flex justify-center lg:hidden">
              <BrandLockup variant="dark" size={38} />
            </div>

            {submitted ? (
              <div className="text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
                  <CheckCircle2 className="h-8 w-8 text-green-600" aria-hidden />
                </div>
                <h2 className="mt-6 text-2xl font-bold tracking-tight text-gray-900">
                  {t('contact.successTitle')}
                </h2>
                <p className="mt-2 text-sm text-gray-500">{t('contact.successBody')}</p>
                <Link
                  to="/login"
                  className="mt-8 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-500"
                >
                  <ArrowLeft className="h-4 w-4" aria-hidden />
                  {t('contact.backToLogin')}
                </Link>
              </div>
            ) : (
              <>
                <div className="mb-8 text-center lg:text-left">
                  <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t('contact.title')}</h2>
                  <p className="mt-1.5 text-sm text-gray-500">{t('contact.subtitle')}</p>
                </div>

                {submitError && (
                  <div role="alert" className="mb-5 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                    <div className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-red-500" />
                    <p className="text-sm text-red-700">{submitError}</p>
                  </div>
                )}

                <form className="space-y-5" onSubmit={handleSubmit(onSubmit)} noValidate>
                  {/* Honeypot (hidden from users, catches bots) */}
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="hidden"
                    {...register('website')}
                  />

                  <div>
                    <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-gray-700">
                      {t('common.name')}
                    </label>
                    <div className="relative">
                      <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden />
                      <input
                        id="name"
                        {...register('name', { required: t('common.name') + ' is required' })}
                        className={inputClass}
                        placeholder={t('auth.namePlaceholder')}
                      />
                    </div>
                    {errors.name && <p className="mt-1.5 text-sm text-red-600">{errors.name.message}</p>}
                  </div>

                  <div>
                    <label htmlFor="company" className="mb-1.5 block text-sm font-medium text-gray-700">
                      {t('contact.company')}
                    </label>
                    <div className="relative">
                      <Building2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden />
                      <input
                        id="company"
                        {...register('company')}
                        className={inputClass}
                        placeholder={t('contact.companyPlaceholder')}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-gray-700">
                      {t('common.email')}
                    </label>
                    <div className="relative">
                      <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden />
                      <input
                        id="email"
                        type="email"
                        autoComplete="email"
                        {...register('email', {
                          required: t('common.email') + ' is required',
                          pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'Invalid email address' },
                        })}
                        className={inputClass}
                        placeholder={t('auth.emailPlaceholder')}
                      />
                    </div>
                    {errors.email && <p className="mt-1.5 text-sm text-red-600">{errors.email.message}</p>}
                  </div>

                  <div>
                    <label htmlFor="phone" className="mb-1.5 block text-sm font-medium text-gray-700">
                      {t('common.phone')}
                    </label>
                    <div className="relative">
                      <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden />
                      <input
                        id="phone"
                        type="tel"
                        autoComplete="tel"
                        {...register('phone')}
                        className={inputClass}
                        placeholder={t('contact.phonePlaceholder')}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-gray-700">
                      {t('contact.message')}
                    </label>
                    <textarea
                      id="message"
                      rows={4}
                      {...register('message', { required: t('contact.message') + ' is required' })}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 transition focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                      placeholder={t('contact.messagePlaceholder')}
                    />
                    {errors.message && <p className="mt-1.5 text-sm text-red-600">{errors.message.message}</p>}
                  </div>

                  <button
                    type="submit"
                    disabled={sending}
                    className="group relative flex w-full items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {sending ? (
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                    ) : (
                      <>
                        {t('contact.submit')}
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
                      </>
                    )}
                  </button>
                </form>

                <p className="mt-8 text-center text-sm text-gray-600">
                  <Link to="/login" className="inline-flex items-center gap-1.5 font-semibold text-primary-600 hover:text-primary-500">
                    <ArrowLeft className="h-4 w-4" aria-hidden />
                    {t('contact.backToLogin')}
                  </Link>
                </p>
              </>
            )}

            <div className="mt-6 border-t border-gray-100 pt-6 text-center">
              <a
                href="https://vuenexa.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-primary-600"
              >
                <Globe className="h-4 w-4" aria-hidden />
                vuenexa.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
